import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers';
import { LayerNames } from '@/modules/common/types/layers';

import { LodGroup } from './types';

export const SPLINES_LOD_GROUP_ID = 'Splines';
export const BOXES_LOD_GROUP_ID = 'Boxes';
export const POSES_LOD_GROUP_ID = 'Poses';
export const VEHICLE_LAYOUTS_LOD_GROUP_ID = 'VehicleLayouts';
export const BUBBLES_LOD_GROUP_ID = 'Bubbles';

export const DEFAULT_BUBBLES_ZOOM_THRESHOLD = 20;
export const DEFAULT_BOXES_ZOOM_THRESHOLD = 40;
export const DEFAULT_VEHICLE_LAYOUTS_ZOOM_THRESHOLD = 100;

export const DEFAULT_TRACKS_ZOOM_THRESHOLD = 150;

export const ZOOM_THRESHOLD_MIN = 1;
export const ZOOM_THRESHOLD_MAX = 10000;

const createLodGroup = ({
  id,
  zoomLevelMin,
  zoomLevelMax,
  tags,
  visible,
}: Omit<LodGroup, 'tagRegExp'>): LodGroup => ({
  id,
  zoomLevelMin,
  zoomLevelMax,
  tags,
  tagRegExp: buildTagMatchRegExp([...tags]),
  visible,
});

/**
 * Legacy export.
 *
 * The new LoD flow is driven by layer definitions in `layers/layerTree.ts`.
 * Keep this temporarily so older imports do not break while the old store is phased out.
 */
export const LOD_GROUPS: LodGroup[] = [
  createLodGroup({
    id: 'Icons',
    zoomLevelMin: 20,
    zoomLevelMax: 100,
    tags: [FDDM_TAG.FORKLIFT, FDDM_TAG.HIGHWAY_ARROW, FDDM_TAG.RACK],
    visible: true,
  }),
  createLodGroup({
    id: 'Connections',
    zoomLevelMin: 20,
    zoomLevelMax: 100,
    tags: [FDDM_TAG.CROSSING, FDDM_TAG.DISTANT_CONNECTION],
    visible: true,
  }),
  createLodGroup({
    id: BUBBLES_LOD_GROUP_ID,
    zoomLevelMin: DEFAULT_BUBBLES_ZOOM_THRESHOLD,
    zoomLevelMax: ZOOM_THRESHOLD_MAX,
    tags: [FDDM_TAG.BUBBLE, FDDM_TAG.BUBBLE_ARROW, FDDM_TAG.BUBBLE_DIVIDER],
    visible: true,
  }),
  createLodGroup({
    id: BOXES_LOD_GROUP_ID,
    zoomLevelMin: DEFAULT_BOXES_ZOOM_THRESHOLD,
    zoomLevelMax: ZOOM_THRESHOLD_MAX,
    tags: [FDDM_TAG.POINT_OF_INTEREST, FDDM_TAG.STATION],
    visible: false,
  }),
  createLodGroup({
    id: 'Mappings',
    zoomLevelMin: 100,
    zoomLevelMax: 450,
    tags: [FDDM_TAG.GATE_MAPPING],
    visible: false,
  }),
  createLodGroup({
    id: SPLINES_LOD_GROUP_ID,
    zoomLevelMin: DEFAULT_TRACKS_ZOOM_THRESHOLD,
    zoomLevelMax: ZOOM_THRESHOLD_MAX,
    tags: [FDDM_TAG.TRACK_PIECE, FDDM_TAG.SWEPT_PATH],
    visible: false,
  }),
  createLodGroup({
    id: VEHICLE_LAYOUTS_LOD_GROUP_ID,
    zoomLevelMin: DEFAULT_VEHICLE_LAYOUTS_ZOOM_THRESHOLD,
    zoomLevelMax: ZOOM_THRESHOLD_MAX,
    tags: [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    visible: false,
  }),
];

/**
 * Legacy export.
 *
 * The new LoD hook reads `FDDM_LAYER_DEFINITIONS` directly.
 */
export const LOD_TRACKED_LAYERS = [
  LayerNames.FLOORPLAN,
  LayerNames.CONNECTIONS,
  LayerNames.CONNECTION_BUBBLES,
  LayerNames.GENERATED_STATIONS,
  LayerNames.GENERATED_POIS,
  LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS,
  LayerNames.TRACKS,
  LayerNames.SWEPT_PATHS,
] as const;
